import {
  evalFolderExpr,
  resolveFormationLayout,
  resolveFormationPageViews,
  type FormationFieldComment,
  type FormationLayout
} from '@st/formation'
import { selViewerPageTransform, type ResolvedPageStructure } from '@st/document-viewer-kit'
import { defSelector } from '@st/redux'
import type { FormConfig } from '@st/ui-config'
import { selRightSidebarMode } from '~/st-doc-viewer/viewer-annotations'
import { selResolvedInputs } from './st-folder-state-selectors'
import type { STFolderModuleState } from './st-folder-types'

/*
 * This module is the Formation viewer's model boundary. It turns folder and
 * DVK state into plain render data before React sees it: visibility, field
 * projection, comment joins, page structure, and sidebar policy all live in
 * this static selector graph. Keep those decisions here so the view remains a
 * function of data and does not construct workflows or selectors while
 * rendering.
 *
 * Layout and page views deliberately share selResolvedInputs. A page may not
 * be made visible from one input snapshot and populated from another.
 */

const EMPTY_FORMATION_LAYOUT: FormationLayout = {
  pages: [],
  pageNumberByPageId: {},
  pageNumberByAnchor: {}
}

export function selLoadedFormationConfig(state: STFolderModuleState): FormConfig | undefined {
  if (state.status != 'ready') return undefined
  const schemaId = state.folderState.schemaId
  if (!schemaId) return undefined

  const configState = state.formationConfig
  if (configState.tag != 'loaded' || configState.schemaId != schemaId) return undefined
  return configState.config
}

const selFormationPriorInputs = (state: STFolderModuleState) => {
  if (state.status != 'ready') return undefined
  return state.folderState.prevInputs
}

const selFormationSettings = (state: STFolderModuleState) => {
  if (state.status != 'ready') return undefined
  return state.folderState.settings
}

export const selFormationLayout = defSelector(
  [selLoadedFormationConfig, selResolvedInputs, selFormationPriorInputs, selFormationSettings],
  (formationConfig, inputs, priorInputs, settings) => {
    if (!formationConfig) return EMPTY_FORMATION_LAYOUT

    return resolveFormationLayout({
      config: formationConfig,
      evalExpr: (expr) =>
        evalFolderExpr(expr, {
          inputs,
          priorInputs: priorInputs ?? {},
          settings: settings ?? {},
          context: {}
        })
    })
  }
)

const selFormationFieldHints = (state: STFolderModuleState) => {
  if (state.status != 'ready') return []
  return state.folderState.fieldHints
}

const selFormationComments = (state: STFolderModuleState) => {
  if (state.status != 'ready') return []
  return state.folderState.comments
}

const selFormationUsers = (state: STFolderModuleState) => {
  if (state.status != 'ready') return []
  return state.folderState.users
}

const selFormationDocument = (state: STFolderModuleState) => {
  if (state.status != 'ready' || !state.selectedDocId) return undefined
  return state.folderState.documents.find((document) => document.id == state.selectedDocId)
}

const selFormationFieldHintsByKey = defSelector([selFormationFieldHints], (fieldHints) =>
  Object.fromEntries(fieldHints.map((hint) => [hint.key, hint.text]))
)

const selFormationFieldComments = defSelector(
  [selFormationComments, selFormationUsers, selFormationDocument],
  (comments, users, document): FormationFieldComment[] => {
    if (!document) return []
    const fieldComments: FormationFieldComment[] = []

    for (const comment of comments) {
      if (comment.documentId != document.id) continue
      if (comment.anchor == undefined) continue
      const author = users.find((user) => user.id == comment.authorId)
      fieldComments.push({
        key: comment.anchor,
        body: comment.content,
        author: author?.name ?? author?.email,
        time: comment.time
      })
    }

    return fieldComments
  }
)

/**
 * Produces one complete page model for each page in the resolved layout.
 * Formation components consume these values directly; they do not reach back
 * into the application store or provider context from the custom renderer.
 */
const selFormationPages = defSelector(
  [
    selFormationLayout,
    selResolvedInputs,
    selFormationPriorInputs,
    selFormationFieldHintsByKey,
    selFormationFieldComments
  ],
  (layout, inputs, priorInputs, fieldHints, fieldComments) =>
    resolveFormationPageViews({
      layout,
      inputs,
      priorInputs,
      fieldHints,
      fieldComments
    })
)

export function selFormationPageView(state: STFolderModuleState, pageNumber: number) {
  const page = selFormationPages(state)[pageNumber - 1]
  if (!page) throw new Error(`Formation page ${pageNumber} has no render model`)
  return page
}

const selViewerPagesStatus = (state: STFolderModuleState) => state.viewer.pagesStatus
const selViewerPages = (state: STFolderModuleState) => state.viewer.pages
const selViewerPagesToRender = (state: STFolderModuleState) =>
  state.viewer.viewportState.pagesToRender
const selViewerState = (state: STFolderModuleState) => state.viewer.viewer

const selFormationViewport = defSelector(
  [selViewerPagesStatus, selViewerPages, selViewerPagesToRender, selViewerState],
  (pagesStatus, pages, pagesToRender, viewer) => ({
    pagesStatus,
    pages,
    pagesToRender,
    pageTransform: selViewerPageTransform(viewer),
    showPages: viewer.viewportMeasurement != null
  })
)

const selFormationRightSidebarMode = (state: STFolderModuleState) =>
  selRightSidebarMode(state.viewer)

export const selFormationViewerView = defSelector(
  [
    selFormationDocument,
    selLoadedFormationConfig,
    selFormationLayout,
    selFormationViewport,
    selFormationRightSidebarMode
  ],
  (document, formationConfig, layout, viewport, rightSidebarMode) => {
    if (!document || !formationConfig) return undefined

    const annotationsEnabled = document.documentTypeId == 'stanfordtax'
    const highlightedPageIds = new Set(
      document.pageGroups.filter((group) => group.metadata.highlight === true).map((group) => String(group.metadata.pageId))
    )
    const pages: Array<{
      structure: ResolvedPageStructure
      highlight: boolean
    }> = []

    for (const structure of viewport.pages) {
      const page = layout.pages[structure.pageNumber - 1]
      if (!page) {
        // Both collections are derived from the same layout snapshot. A miss is
        // an integration bug, not an optional page that the view may omit.
        throw new Error(`Formation page ${structure.pageNumber} has no render model`)
      }
      pages.push({ structure, highlight: highlightedPageIds.has(page.id) })
    }

    return {
      documentName: document.name,
      annotationsEnabled,
      showCommentsSidebar: annotationsEnabled && rightSidebarMode == 'comments',
      viewport: {
        pagesStatus: viewport.pagesStatus,
        pagesToRender: viewport.pagesToRender,
        pageTransform: viewport.pageTransform,
        showPages: viewport.showPages
      },
      pages
    }
  }
)
